import type { Messages } from "../../../../../../../core/api";
import { auth } from "../../../../../../../core/auth";
import { formatCustomFieldValue } from "../../../../../../custom-field/custom-field.utils";
import {
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../../../entityProfileModal/components/EntityProfileSummary";
import type { PatientProfileDisplayData } from "../../../../../patientProfileModal.mocks";
import type { PatientProfilePatient } from "../../../../../patientProfileModal.types";
import type { SummaryDraft, SummaryEditableField } from "../../PatientProfileSummarySection.hooks";
import {
  EditableAddressInstructionsSummaryField,
  EditableBirthDateSummaryField,
  EditableClinicalSeveritySummaryField,
  EditableGenderSummaryField,
  EditableLanguageSummaryField,
  EditableSsnSummaryField,
  EditableTextSummaryField,
} from "./components/EditableSummaryField";
import { PersonalDetailsAddressRows } from "./components/PersonalDetailsAddressRows";
import {
  formatArizonaContingencyPlan,
  formatClinicalSeverity,
  formatLocationGroup,
  formatSsn,
  formatSummaryValue,
  getPatientAddressState,
  getPatientCountyName,
  shouldShowMissingAddressWarning,
} from "./personalDetails.utils";

type AddressAutocompleteDraft = {
  addressText: string;
  components: Messages["AddressComponents"];
};

type UpdatePrimaryAddressMutation = {
  isPending: boolean;
  mutate: (draft: AddressAutocompleteDraft) => void;
};

export type PersonalDetailsProps = {
  activeDraft: SummaryDraft | null;
  activeField: SummaryEditableField | null;
  addressAutocompleteDraft: AddressAutocompleteDraft | null;
  agencyId: number | null;
  displayData: PatientProfileDisplayData;
  isEditing: boolean;
  isOnboardingAgency: boolean;
  patient: PatientProfilePatient;
  showBillingFeature: boolean;
  summaryDraft: SummaryDraft;
  updatePrimaryAddressMutation: UpdatePrimaryAddressMutation;
  onActivateField: (field: SummaryEditableField) => void;
  onCancelField: () => void;
  onCommitField: () => void;
  onCustomizeAddress: () => void;
  onSetAddressAutocompleteDraft: (draft: AddressAutocompleteDraft | null) => void;
  onUpdateField: <TKey extends keyof SummaryDraft>(key: TKey, value: SummaryDraft[TKey]) => void;
};

const textFields = [
  { field: "pimsId", label: "PIMS Id" },
  { field: "orderingPcpNpi", label: "Ordering PCP NPI" },
  { field: "adultServiceWorkerName", label: "Adult Service Worker Name" },
  { field: "adultServiceWorkerEmail", label: "Adult Service Worker Email" },
  { field: "firstName", label: "First name" },
  { field: "middleName", label: "Middle name" },
  { field: "lastName", label: "Last name" },
  { field: "address2", label: "Address 2" },
] satisfies Array<{ field: SummaryEditableField; label: string }>;

export function PersonalDetails(props: PersonalDetailsProps) {
  const {
    activeDraft,
    activeField,
    addressAutocompleteDraft,
    agencyId,
    displayData,
    isEditing,
    isOnboardingAgency,
    patient,
    showBillingFeature,
    summaryDraft,
    updatePrimaryAddressMutation,
    onActivateField,
    onCancelField,
    onCommitField,
    onCustomizeAddress,
    onSetAddressAutocompleteDraft,
    onUpdateField,
  } = props;
  const patientAddressState = getPatientAddressState(patient);
  const customFields = Object.entries(patient.customFields ?? {});
  const canEditSsn = auth.isAllowedTo("show_patient_ssn");

  const renderTextField = (field: typeof textFields[number]) =>
    !isEditing ? (
      <SummaryRow
        key={field.field}
        label={field.label}
        value={formatSummaryValue(patient[field.field as keyof PatientProfilePatient])}
      />
    ) : (
      <EditableTextSummaryField
        key={field.field}
        activeDraft={activeDraft}
        activeField={activeField}
        field={field.field}
        label={field.label}
        summaryDraft={summaryDraft}
        value={summaryDraft[field.field as keyof SummaryDraft] as string}
        onActivateField={onActivateField}
        onCancelField={onCancelField}
        onCommitField={onCommitField}
        onUpdateField={onUpdateField}
      />
    );

  return (
    <SummaryBlock title="Personal Details">
      <SummaryRow label="ID" value={formatSummaryValue(patient.displayId ?? patient.id)} />
      {agencyId === 6205 && (
        <SummaryRow label="External ID" value={formatSummaryValue(patient.externalId)} />
      )}
      {!isEditing || !canEditSsn ? (
        <SummaryRow label="Social Security Number (SSN)" value={formatSsn(patient.ssn)} />
      ) : (
        <EditableSsnSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      {renderTextField(textFields[0])}
      {patientAddressState === "MA" && renderTextField(textFields[1])}
      {patientAddressState === "MI" && (
        <>
          {renderTextField(textFields[2])}
          {renderTextField(textFields[3])}
        </>
      )}
      {renderTextField(textFields[4])}
      {renderTextField(textFields[5])}
      {renderTextField(textFields[6])}
      {!isEditing ? (
        <SummaryRow label="Gender" value={formatSummaryValue(patient.gender)} />
      ) : (
        <EditableGenderSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      {!isEditing ? (
        <SummaryRow label="Birth date" value={formatSummaryValue(patient.dateOfBirth)} />
      ) : (
        <EditableBirthDateSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      <PersonalDetailsAddressRows
        activeField={activeField}
        addressAutocompleteDraft={addressAutocompleteDraft}
        displayData={displayData}
        isEditing={isEditing}
        patient={patient}
        updatePrimaryAddressMutation={updatePrimaryAddressMutation}
        onActivateAddress={() => {
          onSetAddressAutocompleteDraft(null);
          onActivateField("address");
        }}
        onCancelField={onCancelField}
        onCustomizeAddress={onCustomizeAddress}
        onSetAddressAutocompleteDraft={onSetAddressAutocompleteDraft}
      />
      {renderTextField(textFields[7])}
      {!isEditing ? (
        <SummaryRow
          label="Address Instructions"
          value={formatSummaryValue(patient.addressInstructions)}
        />
      ) : (
        <EditableAddressInstructionsSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      <SummaryRow label="County" value={formatSummaryValue(getPatientCountyName(patient))} />
      <SummaryRow label="Location Group" value={formatLocationGroup(patient)} />
      {shouldShowMissingAddressWarning(patient, showBillingFeature) && (
        <SummaryRow
          label="Missing address fields"
          value={patient.missingAddressFields?.join(", ") ?? ""}
        />
      )}
      {!isEditing ? (
        <SummaryRow label="Main language" value={formatSummaryValue(patient.mainLanguage)} />
      ) : (
        <EditableLanguageSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          field="mainLanguage"
          label="Main language"
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      {!isEditing ? (
        <SummaryRow
          label="Secondary language"
          value={formatSummaryValue(patient.secondaryLanguage)}
        />
      ) : (
        <EditableLanguageSummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          field="secondaryLanguage"
          label="Secondary language"
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      {!isEditing ? (
        <SummaryRow
          label="Clinical severity level"
          value={formatClinicalSeverity(patient.clinicalSeverityLevel)}
        />
      ) : (
        <EditableClinicalSeveritySummaryField
          activeDraft={activeDraft}
          activeField={activeField}
          summaryDraft={summaryDraft}
          onActivateField={onActivateField}
          onCancelField={onCancelField}
          onCommitField={onCommitField}
          onUpdateField={onUpdateField}
        />
      )}
      {agencyId === 6205 && patientAddressState === "AZ" && (
        <SummaryRow
          label="Contingency Plan"
          value={formatArizonaContingencyPlan(patient.arizonaContingencyPlanCode)}
        />
      )}
      {isOnboardingAgency && (
        <SummaryRow label="Is Test User" value={formatSummaryValue(patient.isTestUser)} />
      )}
      {customFields
        .filter(([, value]) => value.isVisibleOnPatientProfile)
        .map(([fieldName, value]) => (
          <SummaryRow
            key={fieldName}
            label={value.label}
            value={formatSummaryValue(formatCustomFieldValue(value))}
          />
        ))}
    </SummaryBlock>
  );
}
